import React, { useCallback, useId, useMemo, useState } from "react";
import {
  Box,
  Button,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
  useTheme
} from "@mui/material";
import {
  Badge,
  Bolt,
  DirectionsCar,
  EvStation,
  LocalParking,
  MoreHoriz
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { ReactComponent as WorkplaceIcon } from "../../Booking-Form/assets/avatar.svg";
import { ReactComponent as ConferenceIcon } from "../../Booking-Form/assets/conference.svg";
import { namePerType } from "../../../components/FacilityManager/Components/Toolbars/InfoSidebar/AssignBox/AssignBox";
import { BookingType } from "../../Booking-Form/typings/booking-inputs";
import { dashboard2QuickActionsSx, dashboard2QuietQuickActionButtonSx } from "../dashboard2.styles";
import {
  dashboard2TopRowStatusQuickActionsSx,
  dashboard2NeutralSectionFieldsetSx,
  dashboard2SectionFieldsetLegendIconSx,
  dashboard2QuickActionsEmptySx
} from "../dashboard2-status.styles";
import {
  buildDashboard2QuickActionDescriptors,
  dashboard2QuickActionKey,
  dashboard2QuickActionTestId,
  type Dashboard2QuickActionDescriptor,
  partitionDashboard2QuickActions
} from "../utils/dashboard2-quick-actions.utils";
import { Dashboard2SectionPanel } from "./Dashboard2SectionPanel";

type Dashboard2BookingQuickActionsPanelProps = {
  placeBookingTypes?: readonly BookingType[];
  visitorManagementEnabled: boolean;
  conferenceZoneBookingEnabled: boolean;
  onBookPlaceType?: (bookingType: BookingType) => void;
  onBookAVisitor?: () => void;
  isWideRow?: boolean;
  actionsDisabled?: boolean;
};

const placeQuickActionIcon = (bookingType: BookingType) => {
  if (bookingType === BookingType.WORKPLACE) {
    return <WorkplaceIcon style={{ width: "20px", height: "20px" }} />;
  }

  if (bookingType === BookingType.PARKINGPLACE) {
    return <LocalParking fontSize="medium" />;
  }

  if (bookingType === BookingType.ELECTRICCHARGINGSTATIONPLACE) {
    return <EvStation fontSize="medium" />;
  }

  return <DirectionsCar fontSize="medium" />;
};

const quickActionIcon = (descriptor: Dashboard2QuickActionDescriptor) => {
  if (descriptor.type === "place") {
    return placeQuickActionIcon(descriptor.bookingType);
  }

  if (descriptor.type === "conference") {
    return <ConferenceIcon style={{ width: "20px", height: "20px" }} />;
  }

  return <Badge fontSize="medium" />;
};

export const Dashboard2BookingQuickActionsPanel: React.FC<
  Dashboard2BookingQuickActionsPanelProps
> = ({
  placeBookingTypes = [],
  visitorManagementEnabled,
  conferenceZoneBookingEnabled,
  onBookPlaceType,
  onBookAVisitor,
  isWideRow = false,
  actionsDisabled = false
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const overflowMenuId = useId();
  const [overflowMenuAnchor, setOverflowMenuAnchor] = useState<HTMLElement | null>(null);
  const overflowMenuOpen = overflowMenuAnchor != null;

  const placeQuickActionLabel = useCallback(
    (bookingType: BookingType) => {
      if (bookingType === BookingType.WORKPLACE) {
        return t("_dashboard2_bookAPlace");
      }

      return t("_dashboard2_bookAPlaceType", {
        placeType: t(namePerType(bookingType, t))
      });
    },
    [t]
  );

  const quickActionLabel = useCallback(
    (descriptor: Dashboard2QuickActionDescriptor) => {
      if (descriptor.type === "place") {
        return placeQuickActionLabel(descriptor.bookingType);
      }

      if (descriptor.type === "conference") {
        return t("_dashboard2_bookARoom");
      }

      return t("_dashboard2_bookAVisitor");
    },
    [placeQuickActionLabel, t]
  );

  const runQuickAction = useCallback(
    (descriptor: Dashboard2QuickActionDescriptor) => {
      if (descriptor.type === "place") {
        onBookPlaceType?.(descriptor.bookingType);
        return;
      }

      if (descriptor.type === "conference") {
        if (conferenceZoneBookingEnabled) {
          onBookPlaceType?.(BookingType.CONFERENCEZONE);
        }
        return;
      }

      if (visitorManagementEnabled) {
        onBookAVisitor?.();
      }
    },
    [conferenceZoneBookingEnabled, onBookAVisitor, onBookPlaceType, visitorManagementEnabled]
  );

  const handleCloseOverflowMenu = useCallback(() => {
    setOverflowMenuAnchor(null);
  }, []);

  const handleOpenOverflowMenu = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setOverflowMenuAnchor(event.currentTarget);
  }, []);

  const handleOverflowMenuItemClick = useCallback(
    (descriptor: Dashboard2QuickActionDescriptor) => {
      runQuickAction(descriptor);
      handleCloseOverflowMenu();
    },
    [handleCloseOverflowMenu, runQuickAction]
  );

  const actionDescriptors = useMemo(
    () =>
      buildDashboard2QuickActionDescriptors({
        placeBookingTypes,
        conferenceZoneBookingEnabled,
        visitorManagementEnabled
      }),
    [conferenceZoneBookingEnabled, placeBookingTypes, visitorManagementEnabled]
  );

  const { visible: visibleActions, overflow: overflowActions } = useMemo(
    () => partitionDashboard2QuickActions(actionDescriptors),
    [actionDescriptors]
  );

  const renderQuickActionButton = (descriptor: Dashboard2QuickActionDescriptor) => (
    <Button
      key={dashboard2QuickActionKey(descriptor)}
      variant="outlined"
      color="inherit"
      size="small"
      disabled={actionsDisabled}
      startIcon={quickActionIcon(descriptor)}
      sx={dashboard2QuietQuickActionButtonSx(theme, isWideRow)}
      data-testid={dashboard2QuickActionTestId(descriptor)}
      onClick={() => runQuickAction(descriptor)}
    >
      {quickActionLabel(descriptor)}
    </Button>
  );

  return (
    <Dashboard2SectionPanel
      icon={<Bolt fontSize="small" />}
      title={t("_dashboard2_quickActionsLabel")}
      description={t("_dashboard2_quickActionsDescription")}
      iconSx={dashboard2SectionFieldsetLegendIconSx(theme)}
      panelSx={dashboard2NeutralSectionFieldsetSx(theme)}
      data-testid="dashboard2-booking-quick-actions"
      headerTestId="dashboard2-quick-actions-legend"
    >
      <Box sx={dashboard2TopRowStatusQuickActionsSx(isWideRow)}>
        {actionDescriptors.length === 0 ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={dashboard2QuickActionsEmptySx}
            data-testid="dashboard2-quick-actions-empty"
          >
            {t("_dashboard2_quickActionsEmpty")}
          </Typography>
        ) : (
          <Box
            sx={dashboard2QuickActionsSx(isWideRow)}
            data-testid="dashboard2-quick-actions-list"
            data-layout={isWideRow ? "vertical" : "horizontal"}
          >
            {visibleActions.map(renderQuickActionButton)}

            {overflowActions.length > 0 ? (
              <>
                <Button
                  id="dashboard2-quick-actions-more-button"
                  variant="outlined"
                  color="inherit"
                  size="small"
                  disabled={actionsDisabled}
                  startIcon={<MoreHoriz fontSize="medium" />}
                  sx={dashboard2QuietQuickActionButtonSx(theme, isWideRow)}
                  data-testid="dashboard2-quick-actions-more"
                  aria-haspopup="menu"
                  aria-expanded={overflowMenuOpen}
                  aria-controls={overflowMenuOpen ? overflowMenuId : undefined}
                  aria-label={t("_dashboard2_quickActionsMoreAria", {
                    count: overflowActions.length
                  })}
                  onClick={handleOpenOverflowMenu}
                >
                  {t("_dashboard2_quickActionsMore", { count: overflowActions.length })}
                </Button>
                <Menu
                  id={overflowMenuId}
                  anchorEl={overflowMenuAnchor}
                  open={overflowMenuOpen}
                  onClose={handleCloseOverflowMenu}
                  anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                  transformOrigin={{ vertical: "top", horizontal: "left" }}
                  slotProps={{
                    paper: {
                      sx: {
                        minWidth: overflowMenuAnchor?.offsetWidth ?? undefined
                      }
                    },
                    list: {
                      "aria-labelledby": "dashboard2-quick-actions-more-button"
                    }
                  }}
                >
                  {overflowActions.map(descriptor => (
                    <MenuItem
                      key={dashboard2QuickActionKey(descriptor)}
                      disabled={actionsDisabled}
                      data-testid={`${dashboard2QuickActionTestId(descriptor)}-menu`}
                      onClick={() => handleOverflowMenuItemClick(descriptor)}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        {quickActionIcon(descriptor)}
                      </ListItemIcon>
                      <ListItemText>{quickActionLabel(descriptor)}</ListItemText>
                    </MenuItem>
                  ))}
                </Menu>
              </>
            ) : null}
          </Box>
        )}
      </Box>
    </Dashboard2SectionPanel>
  );
};
